import { useDispatch, useSelector } from "react-redux";
import { setSnackbar } from "reduxStore/state";
import {
  createUser,
  fetchUsers,
  updateUser,
} from "reduxStore/actions/userActions";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { fetchRoles } from "reduxStore/actions/roleActions";
import { debounce } from "lodash";
import {
  fetchDivisionById,
  searchDivisions,
} from "reduxStore/actions/divisionActions";
import { fetchAllLocations } from "reduxStore/actions/locationActions";
import {
  fetchLevelById,
  fetchLevelsForLocation,
} from "reduxStore/actions/levelActions";
import { getPermissionFlags, getUserScope } from "utils/permissions";

export const useUserForm = ({
  editMode,
  setOpen,
  currentUser,
  currentUserName,
  setNewUserId = () => {},
}) => {
  const dispatch = useDispatch();

  const divisionSearchResults = useSelector(
    (state) => state.divisionSearchResults || [],
  );
  const [searchDivisionTerm, setSearchDivisionTerm] = useState("");
  const [valuesLocation, setValuesLocation] = useState(null); // exposed for LocationWatcher

  const levelsForLocation = useSelector((state) => state.levels.data);
  const locationsAll = useSelector((state) => state.locationsAll || []);

  const user = useSelector((state) => state.user);
  const { organisationId, locationIds } = getUserScope(user);
  const location = locationIds[0] || "";

  const { showOrg } = getPermissionFlags(user);

  const roles = useSelector((state) => state.roles);

  const [resolvedDivisions, setResolvedDivisions] = useState([]);
  const [resolvedLevels, setResolvedLevels] = useState([]);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string()
      .email("Enter a valid email")
      .required("Email is required"),
    divisions: Yup.array()
      .of(Yup.object().nullable())
      .min(1, "At least one Department is required"),
    levels: Yup.array()
      .of(Yup.object().nullable())
      .when("roles", {
        is: (roles) => roles?.some((role) => role.name === "Head of Year"),
        then: (schema) => schema.min(1, "At least one Year is required"),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    locations: Yup.array()
      .of(Yup.object().nullable())
      .min(1, "At least one Location is required"),

    roles: Yup.array().min(1, "At least one role is required"),
    status: Yup.string().required("Status is required"),
  });

  const coerceIdArrayToObjectArray = (maybeIdsOrObjects, fallbackObject) => {
    if (Array.isArray(maybeIdsOrObjects) && maybeIdsOrObjects.length > 0) {
      const first = maybeIdsOrObjects[0];

      // If we already have populated objects, keep them.
      if (first && typeof first === "object") {
        return maybeIdsOrObjects;
      }

      // Keep the ids array (we can resolve ids to objects later).
      return maybeIdsOrObjects;
    }

    if (fallbackObject) {
      return [fallbackObject];
    }

    return [];
  };

  const initialValues = {
    firstName: currentUser
      ? currentUser.firstName
      : currentUserName?.firstName || "",
    lastName: currentUser
      ? currentUser.lastName
      : currentUserName?.lastName || "",
    email: currentUser ? currentUser.email : "",
    divisions: coerceIdArrayToObjectArray(
      currentUser?.divisions,
      currentUser?.division,
    ),
    levels: coerceIdArrayToObjectArray(currentUser?.levels, currentUser?.level),
    locations: coerceIdArrayToObjectArray(
      currentUser?.locations,
      currentUser?.location,
    ),
    roles: currentUser ? currentUser.roles : [],
    status: currentUser?.status || "active",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      values.organisation = organisationId;
      const locationsIds =
        values.locations?.map((loc) => loc?._id || loc) || [];
      const divisionsIds =
        values.divisions?.map((div) => div?._id || div) || [];
      const levelsIds = values.levels?.map((lvl) => lvl?._id || lvl) || [];

      values.locations = locationsIds;
      values.divisions = divisionsIds;
      values.levels = levelsIds;

      values.location = locationsIds[0] || null;
      values.division = divisionsIds[0] || null;
      values.level = levelsIds[0] || null;
      values.roles = values.roles.map((role) => role._id);

      let response;
      if (editMode) {
        response = await dispatch(updateUser(currentUser._id, values));
      } else {
        response = await dispatch(createUser(values));
        if (response && response.user && response.user._id) {
          setNewUserId(response.user._id); // Set the new user ID if created
        }
      }

      // Ensure response is an object
      if (!response || typeof response !== "object") {
        throw new Error("Unexpected response format");
      }

      // Determine whether the response was a success or an error
      const isSuccess = response.success && response.message;
      const message = response.message || "An unexpected error occurred";

      dispatch(
        setSnackbar({
          open: true,
          message,
          severity: isSuccess ? "success" : "error",
        }),
      );

      if (isSuccess) {
        setOpen(false);
      }

      const filters = {
        page: 1, // Ensure alignment with API expectations
        pageSize: 10, // API expects 'limit' instead of 'pageSize'
        sort: "",
        search: "",
        location: !showOrg ? location : "",
      };
      dispatch(fetchUsers(filters));
    } catch (error) {
      console.error("Error while deleting user:", error);

      const errorMessage =
        error?.message || "Failed to delete user. Please try again.";

      dispatch(
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        }),
      );
    } finally {
      setSubmitting(false);
      resetForm();
    }
  };

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchAllLocations());
  }, [dispatch]);

  useEffect(() => {
    if (!valuesLocation) return;
    const locationId = valuesLocation?._id || valuesLocation;
    if (!locationId) return;
    dispatch(fetchLevelsForLocation(locationId));
  }, [valuesLocation, dispatch]);

  useEffect(() => {
    if (!editMode) {
      setResolvedDivisions([]);
      return;
    }

    const ids = (currentUser?.divisions || [])
      .map((d) => (typeof d === "string" ? d : d?._id))
      .filter(Boolean);

    const objectDivisions = (currentUser?.divisions || []).filter(
      (d) => d && typeof d === "object",
    );

    const already = new Set(
      [...objectDivisions, ...resolvedDivisions]
        .map((d) => d?._id)
        .filter(Boolean),
    );

    const missing = ids.filter((id) => !already.has(id));
    if (missing.length === 0) {
      if (objectDivisions.length > 0 && resolvedDivisions.length === 0) {
        setResolvedDivisions(objectDivisions);
      }
      return;
    }

    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        missing.map((id) => dispatch(fetchDivisionById(id))),
      );

      if (cancelled) return;

      const fetched = results.filter(Boolean);
      const merged = [...objectDivisions, ...resolvedDivisions, ...fetched];
      const byId = new Map();
      for (const div of merged) {
        if (div?._id) byId.set(div._id, div);
      }
      setResolvedDivisions(Array.from(byId.values()));
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, currentUser?._id, dispatch]);

  useEffect(() => {
    if (!editMode) {
      setResolvedLevels([]);
      return;
    }

    const ids = (currentUser?.levels || [])
      .map((l) => (typeof l === "string" ? l : l?._id))
      .filter(Boolean);

    const objectLevels = (currentUser?.levels || []).filter(
      (l) => l && typeof l === "object",
    );

    const already = new Set(
      [...objectLevels, ...resolvedLevels].map((l) => l?._id).filter(Boolean),
    );

    const missing = ids.filter((id) => !already.has(id));
    if (missing.length === 0) {
      if (objectLevels.length > 0 && resolvedLevels.length === 0) {
        setResolvedLevels(objectLevels);
      }
      return;
    }

    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        missing.map((id) => dispatch(fetchLevelById(id))),
      );

      if (cancelled) return;

      const fetched = results.filter(Boolean);
      const merged = [...objectLevels, ...resolvedLevels, ...fetched];
      const byId = new Map();
      for (const lvl of merged) {
        if (lvl?._id) byId.set(lvl._id, lvl);
      }
      setResolvedLevels(Array.from(byId.values()));
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, currentUser?._id, dispatch]);

  // Debounce the search term to prevent too many API calls
  useEffect(() => {
    const debounceFetch = debounce(() => {
      if (searchDivisionTerm) {
        if (!showOrg) {
          dispatch(searchDivisions({ search: searchDivisionTerm, location }));
        } else {
          dispatch(searchDivisions({ search: searchDivisionTerm }));
        }
      }
    }, 300); // 300ms delay
    debounceFetch();

    // Clearup function to cancel the debounced on unmount
    return () => {
      debounceFetch.cancel();
    };
  }, [searchDivisionTerm, showOrg, location, dispatch]);

  return {
    validationSchema,
    initialValues,
    handleSubmit,
    roles,
    divisionSearchResults,
    resolvedDivisions,
    resolvedLevels,
    setSearchDivisionTerm,
    setValuesLocation,
    levelsForLocation,
    locationsAll,
  };
};
